import { useMemo } from 'react';
import { ActivityFiltersInput, ActivityType } from '@frontend/app/types/globalTypes';
import { useGetActivitiesForProgramQuery } from '@/app/hooks/useGetActivitiesForProgramQuery';
import { ActivityLog } from './constants';
import { GetActivitiesByFilters_activities } from '@/app/queries/types/GetActivitiesByFilters';
import { DateRangeFormData } from '@/app/refresh-components';

// Helper function to calculate date ranges from DateRangeFormData
const getDateRange = (dateRangeData: DateRangeFormData): { startDate?: Date; endDate?: Date } => {
  // If custom date range is provided, use it
  if (dateRangeData.dateRange?.from && dateRangeData.dateRange?.to) {
    return {
      startDate: new Date(dateRangeData.dateRange.from),
      endDate: new Date(dateRangeData.dateRange.to),
    };
  }

  // If allTime, return no date range (shows all data)
  if (dateRangeData.dateType === 'allTime') {
    return {};
  }

  const now = new Date();

  switch (dateRangeData.dateType) {
    case 'thisWeek': {
      // Get start of current week (Monday)
      const startOfWeek = new Date(now);
      const dayOfWeek = now.getDay();
      const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startOfWeek.setDate(now.getDate() - daysToSubtract);
      startOfWeek.setHours(0, 0, 0, 0);
      return { startDate: startOfWeek };
    }
    case 'lastWeek': {
      // Get last week (Monday to Sunday)
      const startOfLastWeek = new Date(now);
      const dayOfWeek = now.getDay();
      const daysToSubtract = dayOfWeek === 0 ? 13 : dayOfWeek + 6;
      startOfLastWeek.setDate(now.getDate() - daysToSubtract);
      startOfLastWeek.setHours(0, 0, 0, 0);

      const endOfLastWeek = new Date(startOfLastWeek);
      endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
      endOfLastWeek.setHours(23, 59, 59, 999);

      return { startDate: startOfLastWeek, endDate: endOfLastWeek };
    }
    case 'lastMonth': {
      // Get last month
      const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      endOfLastMonth.setHours(23, 59, 59, 999);
      return { startDate: startOfLastMonth, endDate: endOfLastMonth };
    }
    case 'monthToDate': {
      // Month to date (from start of current month)
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      startOfMonth.setHours(0, 0, 0, 0);
      return { startDate: startOfMonth };
    }
    case 'lastQuarter': {
      // Get last quarter
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const startOfLastQuarter = new Date(now.getFullYear(), (currentQuarter - 1) * 3, 1);
      const endOfLastQuarter = new Date(now.getFullYear(), currentQuarter * 3, 0);
      endOfLastQuarter.setHours(23, 59, 59, 999);
      return { startDate: startOfLastQuarter, endDate: endOfLastQuarter };
    }
    case 'quarterToDate': {
      // Quarter to date
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const startOfQuarter = new Date(now.getFullYear(), currentQuarter * 3, 1);
      startOfQuarter.setHours(0, 0, 0, 0);
      return { startDate: startOfQuarter };
    }
    case 'last6Months': {
      // Last 6 months
      const sixMonthsAgo = new Date(now);
      sixMonthsAgo.setMonth(now.getMonth() - 6);
      sixMonthsAgo.setHours(0, 0, 0, 0);
      return { startDate: sixMonthsAgo };
    }
    case 'yearToDate': {
      // Year to date
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      startOfYear.setHours(0, 0, 0, 0);
      return { startDate: startOfYear };
    }
    default:
      return {};
  }
};

// Transform GraphQL data to ActivityLog format
const transformActivityData = (activities: GetActivitiesByFilters_activities[]): ActivityLog[] => {
  return activities.map((activity) => ({
    id: activity.id,
    type: activity.type as ActivityType, // Cast to ActivityType enum
    subType: activity.subType,
    programId: activity.programId || undefined,
    memberId: activity.memberId || undefined,
    title: activity.title,
    description: activity.description,
    actions: activity.actions.map((action) => ({
      actionText: action.actionText,
      parameters: action.parameters || {}
    })),
    media: activity.media.map((media) => ({
      type: media.type,
      url: media.url
    })),
    metadata: activity.metadata ? {
      id: activity.metadata.id,
      name: activity.metadata.name,
      stageName: activity.metadata.stageName,
      previousStatusName: activity.metadata.previousStatusName,
      currentStatusName: activity.metadata.currentStatusName,
      userName: activity.metadata.userName,
      userId: activity.metadata.userId,
    } : {},
    createdDate: new Date(activity.createdDate),
    updatedDate: new Date(activity.updatedDate)
  }));
};

export const useActivityData = (projectId: number, dateRangeData: DateRangeFormData, typeFilter: string) => {
  // Calculate date range for GraphQL filters
  const dateRange = useMemo(() => getDateRange(dateRangeData), [dateRangeData]);

  // Build GraphQL query filters
  const filters = useMemo(() => {
    const queryFilters: ActivityFiltersInput = {
      programId: projectId.toString(),
      ...dateRange
    };

    // Add type filter if not 'all'
    if (typeFilter !== 'all') {
      queryFilters.type = typeFilter as ActivityType;
    }

    return queryFilters;
  }, [projectId, dateRange, typeFilter]);

  // Execute GraphQL query
  const { data: queryData, loading, error: queryError, refetch } = useGetActivitiesForProgramQuery({
    variables: { filters },
    skip: !projectId, // Skip query if no projectId
    errorPolicy: 'all',
    fetchPolicy: 'network-only'
  });

  // Transform and memoize the data
  const data = useMemo(() => {
    if (!queryData?.activities) return [];

    const transformedData = transformActivityData(queryData.activities);

    // Sort by createdDate (newest first)
    return transformedData.sort((a, b) => b.createdDate.getTime() - a.createdDate.getTime());
  }, [queryData?.activities]);

  // Transform error message
  const error = queryError ? 'Failed to load activity data' : null;

  return { data, loading, error, refetch };
};
